// `avatar update [--tag <dist-tag>] [--no-sync]` — self-update CLI rồi sync pack TOÀN MÁY.
//
// Mặc định tag `latest` (bản stable). Kênh thử nghiệm: `avatar update --tag next`.
// Flow: so version đang chạy vs version của tag trên registry → khác thì `npm i -g`
// (stdio inherit để user thấy progress/lỗi quyền thật) → RE-EXEC binary MỚI chạy `sync --all`.
//
// #211: `update` nay là lệnh GỘP "đưa máy này lên bản mới", không còn là "cài lại npm package":
//   - nhánh "đã ở bản mới nhất" VẪN sync — version CLI và version pack độc lập, thứ hay đổi
//     là pack;
//   - npm install FAIL ⇒ dừng hẳn, KHÔNG sync (sync bằng CLI cũ ngay sau khi người dùng bấm
//     cập nhật là kết quả nửa vời tệ hơn không làm gì);
//   - exit code của `sync --all` được PROPAGATE nguyên (0/1) — nuốt nó ⇒ script/CI thấy 0
//     trong khi N workspace `failed`.
import type { Command } from "commander";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import {
  AVATAR_BINARY_NAME,
  type ReexecSyncAllResult,
  reexecSyncAllWithBinary,
} from "../lib/reexec-sync-all-after-update.js";
import { resolveAvatarBinaryPath } from "../lib/resolve-avatar-binary-path.js";
import {
  AVATAR_CLI_PACKAGE_NAME,
  fetchVersionForDistTag,
  runNpmGlobalInstall,
} from "../lib/self-update-cli-via-npm-global-install.js";
import { log } from "../lib/terminal-logger.js";

/**
 * Mã thoát của `avatar update` khi đã chạy tới bước sync.
 *
 * `130` KHÔNG đi bằng đường propagate: `stdio: "inherit"` cho con cùng process group nên
 * Ctrl-C bắn cả nhóm — con thường chết vì tín hiệu (`code === null`) chứ không kịp trả mã.
 * Chết vì tín hiệu KHÁC (SIGKILL do OOM…) ⇒ `1`: có chuyện xảy ra, đừng báo thành công.
 */
export function resolveUpdateExitCode(result: ReexecSyncAllResult): number {
  if (result.interrupted) return 130;
  if (result.spawnError !== undefined) return 1;
  if (result.signal !== null) return 1;
  return result.code ?? 1;
}

/** Bước sync sau update. Luôn kết thúc tiến trình — mã thoát là của `sync --all`. */
async function runSyncAllWithFreshBinaryThenExit(): Promise<never> {
  // Resolve LẠI theo TÊN trên PATH: `process.argv[1]` trỏ entry của bản CŨ vừa bị `npm i -g`
  // thay, và trỏ sai hẳn khi npm prefix khác (nvm/volta/homebrew).
  const binary = resolveAvatarBinaryPath(AVATAR_BINARY_NAME);
  if (binary === null) {
    log.error(
      "Đã cài CLI nhưng KHÔNG tìm thấy lệnh `avatar` trên PATH ⇒ bỏ qua bước sync.\n" +
        `  Cài lại tay: npm i -g ${AVATAR_CLI_PACKAGE_NAME}\n` +
        "  Rồi mở terminal mới và chạy: avatar sync --all",
    );
    process.exit(1);
  }

  log.info(`Đồng bộ pack cho mọi workspace đã đăng ký: ${binary} sync --all`);
  const result = await reexecSyncAllWithBinary(binary);

  if (result.spawnError !== undefined) {
    log.error(`Không chạy được '${binary}': ${result.spawnError}`);
  }
  if (result.interrupted) {
    log.warn("`avatar update` dừng vì bị ngắt — bước sync có thể chưa đi hết registry.");
  }
  process.exit(resolveUpdateExitCode(result));
}

export function registerUpdateCommand(program: Command): void {
  program
    .command("update")
    .description("Update avatar CLI (npm i -g) RỒI sync pack cho MỌI workspace đã đăng ký trên máy")
    .option("--tag <dist-tag>", "npm dist-tag muốn theo (latest | next)", "latest")
    .option("--no-sync", "Chỉ update CLI, KHÔNG sync workspace nào (cho script/CI)")
    .action(async (opts: { tag: string; sync: boolean }) => {
      const current = readCliVersion();
      const target = fetchVersionForDistTag(opts.tag);

      if (!target) {
        log.error(
          `Không lấy được version cho tag '${opts.tag}' từ npm registry. ` +
            `Kiểm tra mạng/npm rồi thử lại, hoặc chạy tay: npm i -g ${AVATAR_CLI_PACKAGE_NAME}@${opts.tag}`,
        );
        process.exit(1);
      }

      if (target === current) {
        log.success(`avatar CLI đã ở bản mới nhất của tag '${opts.tag}' (v${current}).`);
        // KHÔNG return: pack có nhịp phát hành riêng, "CLI đã mới" không nói gì về pack.
        if (!opts.sync) return;
        return await runSyncAllWithFreshBinaryThenExit();
      }

      log.info(`Update avatar CLI: v${current} → v${target} (tag '${opts.tag}')...`);
      const code = runNpmGlobalInstall(opts.tag);
      if (code !== 0) {
        log.error(
          `npm install thất bại (exit ${code}). Thử chạy tay: ` +
            `npm i -g ${AVATAR_CLI_PACKAGE_NAME}@${opts.tag}`,
        );
        process.exit(code);
      }
      log.success(`Đã update avatar CLI → v${target}.`);

      if (!opts.sync) return;
      return await runSyncAllWithFreshBinaryThenExit();
    });
}
